import { useEffect, useRef, useState } from 'react';
import { Button } from '@zep-us/zus';
import { ICON_COLOR } from '@zep-us/zus/tokens';
import { BoltSmallRectangleColorVioletIcon, ChevronLeftIcon, ClipboardCheckFillIcon, ConfettiFillIcon, DownloadIcon } from '@zep-us/zus-icons';
import { useRouter } from 'next/router';
import { logger } from '@zep/core-logger';
import Footer from '@/components/Footer';
import { useCountryCode } from '@/shared/hooks/useCountryCode';
import { QuizQuoteDocument } from '../shared/components/QuizQuoteDocument';
import { type IssuedQuizQuote } from '../shared/utils/quizQuoteApi';
import { quoteResultStorage } from '../shared/utils/quoteResultStorage';
import { saveQuoteDocumentPdf } from '../shared/utils/saveQuoteDocumentPdf';
function buildCheckoutUrl(quote: IssuedQuizQuote): string {
  const params = new URLSearchParams({
    plan: quote.product.plan.toUpperCase()
  });
  return `/checkout/method?${params.toString()}`;
}
export function QuoteResultPageView() {
  const router = useRouter();
  const {
    isReady,
    query: {
      key: queryKey
    },
    replace
  } = router;
  const {
    isKR,
    isReady: isCountryCodeReady
  } = useCountryCode();
  const pdfQuoteRef = useRef<HTMLDivElement>(null);
  const [quote, setQuote] = useState<IssuedQuizQuote | null>(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  useEffect(() => {
    if (!isReady || !isCountryCodeReady) return;
    if (!isKR) {
      void replace('/pricing');
      return;
    }
    const key = quoteResultStorage.getKeyFromQuery(queryKey);
    const stored = key ? quoteResultStorage.get(key) : null;
    if (!stored) {
      void replace('/pricing/quote');
      return;
    }
    setQuote(stored);
  }, [isCountryCodeReady, isKR, isReady, queryKey, replace]);
  if (!isCountryCodeReady || !isKR || !quote) {
    return null;
  }
  const handleDownloadPdf = async () => {
    if (!pdfQuoteRef.current) return;
    setIsGeneratingPdf(true);
    try {
      await saveQuoteDocumentPdf({
        element: pdfQuoteRef.current,
        scale: 3,
        imageFormat: 'PNG',
        quality: 1.0,
        fileName: `ZEP QUIZ 견적서_${quote.recipient.organizationName}.pdf`,
        preloadImages: true,
        fixWindowSize: true
      });
    } catch (error) {
      logger.captureError(error, {
        attributes: {
          source: 'pricing/quote/result',
          step: 'pdf_download'
        }
      });
      window.alert('PDF 다운로드 중 문제가 발생했습니다. 다시 시도해 주세요.');
    } finally {
      setIsGeneratingPdf(false);
    }
  };
  const handleCheckout = () => {
    window.open(buildCheckoutUrl(quote), '_blank', 'noopener,noreferrer');
  };
  return <div className="flex min-h-[calc(100dvh-50px)] flex-col bg-neutral font-sans text-body-md-regular tablet-sm:min-h-[calc(100dvh-58px)]" data-sentry-component="QuoteResultPageView" data-sentry-source-file="QuoteResultPageView.tsx">
      <main className="flex-1 bg-neutral pb-[80px]">
        <div className="mx-auto flex w-[1000px] justify-between px-0 pt-2xl max-tablet-lg:w-full max-tablet-lg:px-lg">
          <div className="flex flex-col gap-sm">
            <div className="flex gap-2xs">
              <ClipboardCheckFillIcon width={30} height={30} fill={ICON_COLOR.default} data-sentry-element="ClipboardCheckFillIcon" data-sentry-source-file="QuoteResultPageView.tsx" />
              <h1 className="text-heading-lg-extrabold text-default">
                견적서 발행 완료
              </h1>
            </div>
            {quote.pricing.discounts.length > 0 ? <div className="flex items-center gap-2xs pl-3xs">
                <ConfettiFillIcon size="sm" fill={ICON_COLOR.positive} />
                <span className="text-body-md-semibold text-neutral">
                  {quote.pricing.discounts.map(discount => discount.label).join(', ')}
                  이 적용됐어요.
                </span>
              </div> : null}
            {quote.product.pricingTypeNotice ? <p className="pl-3xs text-body-md-medium text-alternative">
                {quote.product.pricingTypeNotice.message}
              </p> : null}
          </div>
        </div>

        <div className="mx-auto mt-xl flex w-[1000px] justify-between gap-2xl px-0 max-tablet-lg:w-full max-tablet-lg:flex-col max-tablet-lg:px-lg">
          <div className="w-[560px] shrink-0 max-tablet-lg:w-full max-tablet-lg:max-w-[560px]">
            <QuizQuoteDocument quote={quote} data-sentry-element="QuizQuoteDocument" data-sentry-source-file="QuoteResultPageView.tsx" />
          </div>

          <div className="w-[400px] shrink-0 max-tablet-lg:w-full max-tablet-lg:max-w-[560px]">
            <Button variant="contained" intent="alternative" size="lg" className="w-full" onClick={() => window.history.back()} data-sentry-element="Button" data-sentry-source-file="QuoteResultPageView.tsx">
              <ChevronLeftIcon size="md" data-sentry-element="ChevronLeftIcon" data-sentry-source-file="QuoteResultPageView.tsx" />
              견적서 정보 재입력
            </Button>
            <Button variant="contained" intent="strong" size="lg" className="mt-4 w-full" disabled={isGeneratingPdf} onClick={handleDownloadPdf} data-sentry-element="Button" data-sentry-source-file="QuoteResultPageView.tsx">
              <DownloadIcon size="md" data-sentry-element="DownloadIcon" data-sentry-source-file="QuoteResultPageView.tsx" />
              {isGeneratingPdf ? 'PDF 생성 중' : 'PDF 다운로드'}
            </Button>

            <div className="mt-md flex flex-col items-center gap-sm self-stretch rounded-md bg-default p-lg shadow-xs">
              <BoltSmallRectangleColorVioletIcon size="xl" data-sentry-element="BoltSmallRectangleColorVioletIcon" data-sentry-source-file="QuoteResultPageView.tsx" />
              <div className="text-center text-heading-md-bold text-default">
                플랜을 바로 시작해보세요
              </div>
              <div className="mb-sm text-center text-body-md-regular text-default">
                카드 결제는 바로 진행할 수 있어요. <br />
                계좌이체는 <strong>1:1 문의</strong>를 통해 도와드릴게요.
              </div>
              <Button variant="contained" intent="gradient" size="lg" className="w-full" onClick={handleCheckout} data-sentry-element="Button" data-sentry-source-file="QuoteResultPageView.tsx">
                결제 바로가기
              </Button>
            </div>
          </div>
        </div>

        {/* 화면 밖에 세워 둔 PDF 캡처용 문서(인쇄 여백 변형). */}
        <div className="pointer-events-none fixed left-[-10000px] top-0 w-[560px]" aria-hidden="true">
          <QuizQuoteDocument ref={pdfQuoteRef} quote={quote} variant="pdf" data-sentry-element="QuizQuoteDocument" data-sentry-source-file="QuoteResultPageView.tsx" />
        </div>
      </main>
      <Footer className="bg-default" data-sentry-element="Footer" data-sentry-source-file="QuoteResultPageView.tsx" />
    </div>;
}